import type { ConsentState } from '~/stores/consent'
import { sanitizeUrl } from '~/utils/tracking-pii'
import type { Attribution, AttributionTouch } from '../../server/utils/tracking-meta'

export type { Attribution, AttributionTouch }

const FIRST_KEY = 'tracking_attribution_first'
const LAST_KEY = 'tracking_attribution_last'
export const FIRST_TOUCH_TTL_MS = 90 * 24 * 60 * 60 * 1000

const CAMPAIGN_PARAMS = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
  'gclid', 'gbraid', 'wbraid', 'fbclid',
] as const

/** The source of one landing, read from its URL and referrer. */
export function buildTouch(href: string, referrer: string, now: number): AttributionTouch {
  const url = new URL(href)
  const touch: AttributionTouch = {
    landing_page: sanitizeUrl(href),
    referrer: null,
    ts: now,
  }
  for (const key of CAMPAIGN_PARAMS) {
    const value = url.searchParams.get(key)?.trim()
    if (value) touch[key] = value.slice(0, 500)
  }
  if (touch.fbclid) touch.fbclid_ts = now

  if (referrer) {
    try {
      if (new URL(referrer).host !== url.host) touch.referrer = sanitizeUrl(referrer)
    }
    catch {
      // not a URL: ignore
    }
  }
  return touch
}

/** A landing from a campaign or another site starts a new last touch; an internal reload does not. */
export function hasSource(touch: AttributionTouch): boolean {
  return touch.referrer !== null || CAMPAIGN_PARAMS.some(key => !!touch[key])
}

export function resolveAttribution(
  touch: AttributionTouch,
  stored: { first: AttributionTouch | null; last: AttributionTouch | null },
  now: number,
): Attribution {
  const storedFirst = stored.first && now - stored.first.ts < FIRST_TOUCH_TTL_MS ? stored.first : null
  return {
    first: storedFirst ?? touch,
    last: hasSource(touch) || !stored.last ? touch : stored.last,
  }
}

let current: Attribution | null = null

function readJson(storage: Storage, key: string): AttributionTouch | null {
  try {
    const raw = storage.getItem(key)
    return raw ? (JSON.parse(raw) as AttributionTouch) : null
  }
  catch {
    return null
  }
}

function writeStored(attribution: Attribution) {
  try {
    if (attribution.first) localStorage.setItem(FIRST_KEY, JSON.stringify(attribution.first))
    if (attribution.last) sessionStorage.setItem(LAST_KEY, JSON.stringify(attribution.last))
  }
  catch {
    // storage unavailable: the in-memory copy still goes with the forms
  }
}

export function clearStoredAttribution() {
  try {
    localStorage.removeItem(FIRST_KEY)
    sessionStorage.removeItem(LAST_KEY)
  }
  catch {
    // storage unavailable: nothing stored
  }
}

/**
 * Reads the landing once per page load. Storage is used only with consent; pending
 * behaves like a rejection, so without consent only this visit (in memory) is reported.
 */
export function initAttribution(consent: ConsentState) {
  if (import.meta.server || current) return
  const now = Date.now()
  const touch = buildTouch(window.location.href, document.referrer, now)
  const stored = consent === 'accepted'
    ? { first: readJson(localStorage, FIRST_KEY), last: readJson(sessionStorage, LAST_KEY) }
    : { first: null, last: null }
  current = resolveAttribution(touch, stored, now)
  syncAttributionConsent(consent)
}

/** Persists on accept (also a late accept in the same visit), clears on reject or withdrawal. */
export function syncAttributionConsent(consent: ConsentState) {
  if (import.meta.server) return
  if (consent === 'accepted') {
    if (current) writeStored(current)
  }
  else {
    clearStoredAttribution()
  }
}

export function getAttribution(): Attribution | null {
  return current
}
